import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { BLOG_POSTS, fmtPostDate } from "../data/blogPosts";

/** Sync <title> + basic meta on mount / route change. Lightweight in place of
 *  react-helmet since we don't need SSR-friendly rewrites for the SPA. */
function useDocumentSEO({ title, description }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", description);
    }
  }, [title, description]);
}

export default function Blog() {
  useDocumentSEO({
    title: "Blog | GenQuotes",
    description:
      "Practical insights for small service businesses — quoting, communication, and getting your time back.",
  });

  const posts = [...BLOG_POSTS].sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt)
  );

  return (
    <div className="relative bg-[#0A1628] text-[#F8FAFC] py-16 sm:py-20 min-h-[70vh]">
      <div className="absolute inset-0 so-grid opacity-20 pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] so-glow-blue pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
            Blog
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Notes on running a smarter service business.
          </h1>
          <p className="mt-4 text-[#94A3B8] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Practical ideas on quoting, communication, and the tools that give
            small business owners their time back.
          </p>
        </div>

        <div className="space-y-6" data-testid="blog-index-list">
          {posts.map((post) => (
            <article
              key={post.slug}
              data-testid={`blog-card-${post.slug}`}
              className="so-card rounded-2xl p-6 sm:p-8 hover:border-[#06B6D4]/40 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] uppercase tracking-widest text-[#94A3B8] font-mono">
                <span>{fmtPostDate(post.publishedAt)}</span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={11} className="text-[#06B6D4]" />
                  {post.readingTime}
                </span>
              </div>
              <h2 className="mt-3 font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-[#F8FAFC]">
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-title-${post.slug}`}
                  className="hover:text-[#06B6D4] transition-colors"
                >
                  {post.title}
                </Link>
              </h2>
              <p className="mt-3 text-[#94A3B8] text-[15px] leading-relaxed">
                {post.excerpt}
              </p>
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-readmore-${post.slug}`}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#EC4899] hover:text-[#F8FAFC] transition-colors"
              >
                Read more <ArrowRight size={14} />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
